import {
  SidebarList,
  SidebarListItemProps,
} from "@/lib/Components/SideBarList/SideBarList";
import { useState } from "react";
import { useInfiniteGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { keepPreviousData } from "@tanstack/react-query";
import {
  WorkOrdersQueryListItem,
  workOrderListQuery,
} from "@/app/ProDrivers/WorkOrders/GraphQL/workOrdersQuery";
import { getWorkOrderListQueryVariables } from "@/app/ProDrivers/WorkOrders/utils/getWorkOrderListQueryVariables";
import { workOrderStatusMap } from "@/app/ProDrivers/WorkOrders/utils/workOrderStatusMap";
import { WorkOrderStatus } from "@/gql/graphql";
import { MultiSelectFilter } from "@/lib/Components/DataTable/Filters/MultiSelectFilter";
import { SupplierFilter } from "@/lib/Components/DataTable/Filters/SupplierFilter";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import { Badge } from "@/components/catalyst/badge";
import { Highlighter } from "@/lib/Components/Highlighter";
import { formatDate } from "@/lib/Formatters/formatDate";
import { formatDateTimeSinceNow } from "@/lib/Formatters/formatDateTimeSinceNow";
import { SupplierIcon } from "@/app/Suppliers/Components/SupplierIcon";
import { useTranslations } from "use-intl";
import { ArrowLeftRightIcon } from "lucide-react";
import {
  BuildingOfficeIcon,
  CalendarDaysIcon,
} from "@heroicons/react/24/outline";

export function WorkOrderListScreen() {
  const t = useTranslations("workOrder");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<WorkOrderStatus[]>([]);
  const { supplierId: globalSupplierId } = useGlobalSupplier();
  const [filterSupplierId, setFilterSupplierId] = useState<string | null>(null);

  const { data, isFetching, isFetchingNextPage, fetchNextPage } =
    useInfiniteGqlQuery(
      workOrderListQuery,
      getWorkOrderListQueryVariables({
        search,
        status,
        supplierId: globalSupplierId ?? filterSupplierId,
      }),
      {
        placeholderData: keepPreviousData,
        initialPageParam: {
          page: 1,
        },
        getNextPageParam: (lastPage) => {
          return lastPage.workOrders.paginatorInfo.hasMorePages
            ? { page: lastPage.workOrders.paginatorInfo.currentPage + 1 }
            : undefined;
        },
      },
    );

  const items = data?.pages.flatMap((page) => page.workOrders.data) ?? [];
  const totalCount = data?.pages.at(0)?.workOrders.paginatorInfo.total ?? 0;

  return (
    <SidebarList<WorkOrdersQueryListItem>
      items={items}
      totalCount={totalCount}
      isFetching={isFetching}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
      search={search}
      onSearchChange={setSearch}
      listRoute="/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders"
      itemNode={(item) => <WorkOrderItem model={item} search={search} />}
      linkProps={(item) => ({
        to: "/$tenant/$orgId/supplier/{-$supplierId}/pro-drivers/work-orders/$id",
        params: (prev) => ({
          ...prev,
          id: item.id,
        }),
      })}
      onClearFilters={() => {
        setSearch("");
        setStatus([]);
        setFilterSupplierId(null);
      }}
      filters={
        <div className="flex flex-wrap gap-2">
          <MultiSelectFilter
            label={t("filters.status")}
            options={Object.values(WorkOrderStatus).map((value) => ({
              label: t(`status.${value}`),
              value,
            }))}
            selected={status}
            onChange={setStatus}
          />
          {globalSupplierId ? null : (
            <SupplierFilter
              label={t("supplier")}
              selected={filterSupplierId}
              onChange={setFilterSupplierId}
            />
          )}
        </div>
      }
    />
  );
}

function WorkOrderItem({
  model,
  search,
}: SidebarListItemProps<WorkOrdersQueryListItem> & { search: string }) {
  const t = useTranslations("workOrder");
  const { hasSupplier } = useGlobalSupplier();

  return (
    <div className="space-y-1.5 truncate">
      <div className="flex items-start justify-between gap-2">
        <p className="truncate text-sm font-semibold text-gray-900 group-hover:text-blue-700">
          <Highlighter searchString={search}>{model.reference}</Highlighter>
        </p>
        <Badge color={workOrderStatusMap[model.status]}>
          {t(`status.${model.status}`)}
        </Badge>
      </div>

      {hasSupplier ? null : (
        <p className="flex items-center truncate text-xs text-gray-600">
          <SupplierIcon
            model={model.supplier}
            className="mr-1.5 size-3.5 shrink-0 rounded-sm"
            fallback={
              <BuildingOfficeIcon className="mr-1.5 inline h-3.5 w-3.5 shrink-0 text-gray-500" />
            }
          />
          <Highlighter searchString={search}>{model.supplier.name}</Highlighter>
        </p>
      )}

      <p className="flex items-center truncate text-xs text-gray-600">
        <ArrowLeftRightIcon className="mr-1.5 inline h-3.5 w-3.5 shrink-0 text-gray-500" />
        {`${model.departureAddress.city} - ${model.deliveryAddress.city}`}
      </p>

      <p className="flex items-center truncate text-xs text-gray-600">
        <CalendarDaysIcon className="mr-1.5 inline h-3.5 w-3.5 shrink-0 text-gray-500" />
        {formatDate(model.depart_after)}
      </p>

      <p>
        <time
          dateTime={model.created_at}
          className="text-xs italic text-gray-500"
        >
          {formatDateTimeSinceNow(model.created_at)}
        </time>
      </p>
    </div>
  );
}
